// Single source of truth for the 5-tier ranking. Rank is driven by the
// number of UNIQUE stamps a user has earned.

export type Tier = {
  /** Canonical English name. UI should localize via `tier_{short}` i18n
   *  keys; this stays as the English fallback / non-UI reference. */
  name: string;
  short: string;
  /** Human-readable stamp range, e.g. "0–4 Stamps" (English fallback). */
  range: string;
  /** Numeric part of the range only, e.g. "0–4" / "36+". UI composes the
   *  localized label via the `tier_range_stamps` i18n key so the word
   *  "Stamps" translates while the digits stay put. */
  stampRange: string;
  /** Inclusive minimum unique-stamp count to hold this tier. */
  min: number;
  /** Crest fill color. */
  crest: string;
  /** True for the top tier (dark card treatment). */
  top?: boolean;
};

export const TIERS: Tier[] = [
  { name: 'Junior Member', short: 'JUNIOR', range: '0–4 Stamps', stampRange: '0–4', min: 0, crest: '#c7b3a8' },
  { name: 'Explorer', short: 'EXPLORER', range: '5–11 Stamps', stampRange: '5–11', min: 5, crest: '#9a7f63' },
  { name: 'Seasoned Traveler', short: 'SEASONED', range: '12–20 Stamps', stampRange: '12–20', min: 12, crest: '#d4685f' },
  { name: 'Master', short: 'MASTER', range: '21–35 Stamps', stampRange: '21–35', min: 21, crest: '#f0b14e' },
  { name: 'Guru of Armenia', short: 'GURU', range: '36+ Stamps', stampRange: '36+', min: 36, crest: '#2b2220', top: true },
];

export type RankProgress = {
  tier: Tier;
  nextTier: Tier | null;
  /** Unique stamps still needed to reach nextTier (0 at top tier). */
  toNext: number;
  /** Progress through the current tier band, 0..1. */
  progress: number;
};

/** Resolve a user's rank from their unique earned-stamp count. */
export function getRank(uniqueStamps: number): RankProgress {
  let idx = 0;
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (uniqueStamps >= TIERS[i].min) {
      idx = i;
      break;
    }
  }

  const tier = TIERS[idx];
  const nextTier = TIERS[idx + 1] ?? null;

  if (!nextTier) {
    return { tier, nextTier: null, toNext: 0, progress: 1 };
  }

  const band = nextTier.min - tier.min;
  const into = uniqueStamps - tier.min;
  return {
    tier,
    nextTier,
    toNext: Math.max(0, nextTier.min - uniqueStamps),
    progress: Math.min(1, Math.max(0, into / band)),
  };
}
